import * as Yup from "yup";
import { SettlementCase } from "src/types/cases";
import { FC } from "react";
import { Box } from "@mui/material";
import { Form, Formik } from "formik";
import {
  FormFieldsGroup,
  FormSection,
} from "src/pages/UserDashboard/WorkPackets/WorkPacketDetailsPopup/Summary/components";
import { CustomerInfo } from "./Settlement/CustomerInfo";
import { CaseInfo } from "./Settlement/CaseInfo";
import { SettlementInfo } from "./Settlement/SettlementInfo";
import { FileUploadContext } from "../../WorkPackets/WorkPacketDetailsPopup/Summary/FileUploadContext";
import { FileUploadBox } from "src/pages/UserDashboard/WorkPackets/WorkPacketDetailsPopup/Summary/components/FileUploadBox";
import toast from "react-hot-toast";
import { CaseType } from "src/pages/UserDashboard/WorkPackets/Cases/case-types";
import { getUpdateParams } from "./hooks/getUpdateParams";
import { useCaseUpdate } from "./hooks/useCaseUpdate";
import { CaseFormActions } from "./components/CaseFormActions";
import { CaseWorkFlags } from "src/pages/UserDashboard/Cases/CaseDetail/CaseWorkFlags.tsx";
import { CaseDetailsKeys } from "../mappers/mapServerToLocalCase";
import { CaseStatus, mandatoryFieldsByStage, ShortagesSettlementsCaseStage as SettlementStage } from "../api/constants";
import type { Dayjs } from "dayjs";
import { CorrespondenceWorkFlag } from "../../WorkPackets/Cases/correspondence-work-flags";

interface CaseSettlementDetailsProps {
  caseItem: SettlementCase;
  correspondenceWorkFlags: CorrespondenceWorkFlag[];
  caseType: CaseType;
  isRefreshing: boolean;
}

const settlementValidationSchema = Yup.lazy((values: SettlementCase) => {
  const requiredFields = values.stage ? mandatoryFieldsByStage[values.stage as SettlementStage] : [];

  const baseSchema: Record<string, Yup.Schema> = {
    [CaseDetailsKeys.Stage]: Yup.string()
      .test(
        "stageMismatchWithStatus",
        "If case status is Waiting for Payment, settlement stage must match.",
        (stage, ctx) => {
          const status = ctx.parent[CaseDetailsKeys.Status];
          if (status === CaseStatus.WaitingForPayment && stage !== SettlementStage.WAITING_FOR_PAYMENT) return false;
          return true;
        },
      )
      .test(
        "stageRequiredForDroppedSettlement",
        "If case status is Dropped Settlement, stage must be Settlement Closed without Resolution.",
        (stage, ctx) => {
          const status = ctx.parent[CaseDetailsKeys.Status];
          if (status === CaseStatus.DroppedSettlement && stage !== SettlementStage.SETTLEMENT_CLOSED_WITHOUT_RESOLUTION)
            return false;
          return true;
        },
      ),
    [CaseDetailsKeys.Status]: Yup.string().test(
      "statusMismatchWithStage",
      "If settlement stage is Waiting for Payment, case status must match.",
      (status, ctx) => {
        const stage = ctx.parent[CaseDetailsKeys.Stage];
        if (stage === SettlementStage.WAITING_FOR_PAYMENT && status !== CaseStatus.WaitingForPayment) return false;
        return true;
      },
    ),
    [CaseDetailsKeys.SecondOfferAmount]: Yup.number()
      .nullable()
      .test(
        "missingSecondOfferAmount",
        "If second offer date is set, second offer amount must also be set.",
        (secondAmount, ctx) => {
          const secondDate = ctx.parent[CaseDetailsKeys.SecondOfferDate];
          if (secondDate && !secondAmount) return false;
          return true;
        },
      ),
    [CaseDetailsKeys.SecondOfferDate]: Yup.mixed<Dayjs>()
      .nullable()
      .test(
        "missingSecondOfferDate",
        "If second offer amount is set, second offer date must also be set.",
        (secondDate, ctx) => {
          const secondAmount = ctx.parent[CaseDetailsKeys.SecondOfferAmount];
          if (secondAmount && (!secondDate || !secondDate.isValid())) return false;
          return true;
        },
      ),
    [CaseDetailsKeys.ThirdOfferAmount]: Yup.number()
      .nullable()
      .test(
        "missingThirdOfferAmount",
        "If third offer date is set, third offer amount must also be set.",
        (thirdAmount, ctx) => {
          const thirdDate = ctx.parent[CaseDetailsKeys.ThirdOfferDate];
          if (thirdDate && !thirdAmount) return false;
          return true;
        },
      ),
    [CaseDetailsKeys.ThirdOfferDate]: Yup.mixed<Dayjs>()
      .nullable()
      .test(
        "missingThirdOfferDate",
        "If third offer amount is set, third offer date must also be set.",
        (thirdDate, ctx) => {
          const thirdAmount = ctx.parent[CaseDetailsKeys.ThirdOfferAmount];
          if (thirdAmount && (!thirdDate || !thirdDate.isValid())) return false;
          return true;
        },
      ),
    [CaseDetailsKeys.AlignedBalAmount]: Yup.number()
      .test(
        "lessThanSubmission",
        "Error! The Aligned Balance Amount should be less than or equal to the Submission Amount",
        (value, ctx) => {
          const submissionAmount = ctx.parent[CaseDetailsKeys.SubmissionAmount];
          if (submissionAmount == 0) return true;
          return !value || !submissionAmount || value <= submissionAmount;
        },
      )
      .test(
        "greaterThanPaidAmount",
        "Error! The Aligned Balance Amount should be greater than the Paid Amount",
        (value, ctx) => {
          const paidAmount = ctx.parent[CaseDetailsKeys.PaidAmountBeforeOpenBalanceAlignment];
          return !value || (!paidAmount && paidAmount != 0) || value > paidAmount;
        },
      )
      .test(
        "greaterThanFirstOffer",
        "Aligned Balance Amount should be greater than the 1st offer amount",
        (value, ctx) => {
          const firstOfferAmount = ctx.parent[CaseDetailsKeys.FirstOfferAmount];
          const secondOfferAmount = ctx.parent[CaseDetailsKeys.SecondOfferAmount];
          const thirdOfferAmount = ctx.parent[CaseDetailsKeys.ThirdOfferAmount];

          if (secondOfferAmount !== 0 || thirdOfferAmount !== 0) {
            return true;
          }

          return !value || !firstOfferAmount || value > firstOfferAmount;
        },
      )
      .test(
        "greaterThanSecondOffer",
        "Aligned Balance Amount should be greater than the 2nd offer amount",
        (value, ctx) => {
          const secondOfferAmount = ctx.parent[CaseDetailsKeys.SecondOfferAmount];
          const thirdOfferAmount = ctx.parent[CaseDetailsKeys.ThirdOfferAmount];

          if (thirdOfferAmount !== 0) {
            return true;
          }

          return !value || !secondOfferAmount || value > secondOfferAmount;
        },
      )
      .test(
        "greaterThanThirdOffer",
        "Aligned Balance Amount should be greater than the 3rd offer amount",
        (value, ctx) => {
          const thirdOfferAmount = ctx.parent[CaseDetailsKeys.ThirdOfferAmount];
          return !value || !thirdOfferAmount || value > thirdOfferAmount;
        },
      ),
    [CaseDetailsKeys.AlignedBalDate]: Yup.date().test(
      "afterSubmissionDate",
      "Error! The Aligned Balance Date should be greater than the Submission Date",
      (value, ctx) => {
        const submissionDate = ctx.parent[CaseDetailsKeys.SubmissionDate];
        if (!value || !submissionDate) return true;
        return value > submissionDate;
      },
    ),
    [CaseDetailsKeys.FinalAmount]: Yup.number().test(
      "finalAmountRequiredWhenStatusIsWaitingForPayment",
      "Error! The Final Amount is required when status is set to Waiting For Payment",
      (value, ctx) => {
        const status = ctx.parent[CaseDetailsKeys.Status];
        if (status !== CaseStatus.WaitingForPayment) return true;

        return !!value;
      },
    ),
  };

  requiredFields.forEach(field => {
    baseSchema[field] = (baseSchema[field] || Yup.mixed()).required("This field is required for this stage");
  });

  return Yup.object().shape(baseSchema);
});

export const CaseSettlementDetails: FC<CaseSettlementDetailsProps> = ({
  caseItem,
  correspondenceWorkFlags,
  caseType,
  isRefreshing,
}) => {
  const { attachmentsUploadStore, attachmentUploadMutation, caseUpdateMutation, isSubmitting } = useCaseUpdate(
    caseItem.id,
  );

  const onSettlementSubmit = async (data: SettlementCase) => {
    try {
      const pendingUploads = attachmentsUploadStore.fileUploads.filter(upload => upload.status === "pending");

      if (pendingUploads.length > 0) {
        await attachmentUploadMutation.mutateAsync({
          caseItem,
          fileUploads: pendingUploads,
          setFileUploadStatus: attachmentsUploadStore.setFileUploadStatus,
        });
      }

      const updateParams = getUpdateParams(caseItem, data);
      updateParams["CASE_OWNER_ID"] = updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS?.CASE_OWNER_ID;
      updateParams["BACKUP_CASE_OWNER_ID"] = updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS?.BACKUP_CASE_OWNER_ID;
      updateParams["STATUS"] = updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS?.STATUS;
      updateParams["AMAZON_CASE_CREATED_DATE"] = !data.submissionDate?.isSame(caseItem.submissionDate)
        ? data.submissionDate?.format("YYYY-MM-DD")
        : undefined;

      if (updateParams.SHORTAGES_SETTLEMENT_OFFERS?.length === 0) {
        delete updateParams.SHORTAGES_SETTLEMENT_OFFERS;
      }

      if (
        updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS &&
        "AMAZON_CASE_CREATED_DATE" in updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS
      ) {
        delete updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS.AMAZON_CASE_CREATED_DATE;
      }

      if (
        updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS &&
        "CASE_OWNER_ID" in updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS
      ) {
        delete updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS.CASE_OWNER_ID;
      }

      if (
        updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS &&
        "BACKUP_CASE_OWNER_ID" in updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS
      ) {
        delete updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS.BACKUP_CASE_OWNER_ID;
      }

      if (
        updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS &&
        "STATUS" in updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS
      ) {
        delete updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS.STATUS;
      }

      if (
        updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS &&
        "CASE_FILING_USER" in updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS
      ) {
        updateParams["CASE_FILING_USER"] = updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS.CASE_FILING_USER;
        delete updateParams.SHORTAGES_SETTLEMENT_CASE_DETAILS.CASE_FILING_USER;
      }

      const hasChanges = Object.keys(updateParams).some(key => {
        const value = updateParams[key as keyof typeof updateParams];
        return value !== undefined && value !== null && (typeof value !== "object" || Object.keys(value).length > 0);
      });

      if (!hasChanges) {
        toast("No changes to update");
        return;
      }

      return caseUpdateMutation.mutateAsync({
        caseId: caseItem.id,
        caseType,
        updateParams,
      });
    } catch (error) {
      toast.error("Failed to update case");
    }
  };

  return (
    <Box padding={4}>
      <FileUploadContext.Provider value={attachmentsUploadStore}>
        <Formik
          initialValues={{
            ...caseItem,
            correspondenceWorkFlags: correspondenceWorkFlags,
          }}
          enableReinitialize
          onSubmit={onSettlementSubmit}
          validationSchema={settlementValidationSchema}
          validateOnMount
          validateOnChange
        >
          <Box display={"grid"} gridTemplateColumns={"100%"}>
            <Form style={{ display: "contents" }}>
              <FormSection title="General Case Info" id="general-case-info">
                {isRefreshing && <Box paddingTop={2}>Refreshing...</Box>}
                <Box {...(isRefreshing && { sx: { opacity: 0.5 }, inert: "" })}>
                  <CustomerInfo />
                  <CaseInfo />
                  <CaseWorkFlags />
                  <CaseFormActions isSubmitting={isSubmitting} isRefetching={isRefreshing} />
                </Box>
              </FormSection>

              <FormSection title="Settlement" id="settlement-info">
                {isRefreshing && <Box paddingTop={2}>Refreshing...</Box>}
                <Box {...(isRefreshing && { sx: { opacity: 0.5 }, inert: "" })}>
                  <SettlementInfo />
                  <Box display="grid" gap={4} gridTemplateColumns="1fr 1fr">
                    <FormFieldsGroup withDivider title="Attachments" id="attachments">
                      <FileUploadBox accept="csv" acceptHint="CSV" />
                    </FormFieldsGroup>
                  </Box>
                </Box>
                <CaseFormActions isSubmitting={isSubmitting} isRefetching={isRefreshing} />
              </FormSection>
            </Form>
          </Box>
        </Formik>
      </FileUploadContext.Provider>
    </Box>
  );
};
